import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import allActions from "../actions";
import sha256 from "js-sha256";
import LoadingSpinner from "./Spinner";
import ProfileForm from "./UserProfileForm";
import { countries, starSigns, usStates } from "../services/functions";

const UserSection = ({ searchterm }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const hash = sha256(searchterm);
  const accessToken = localStorage.getItem("accessToken");

  const attributionLink = "/leadtrackingdata/" + (searchterm ? searchterm : "");

  const apiLeadUrl = `${process.env.REACT_APP_CSAPI_BASE_URL}/lead?hash=${hash}`;
  const apiCustomerUrl = `${process.env.REACT_APP_CSAPI_BASE_URL}/customer?hash=${hash}`;

  const [isLoading, setIsLoading] = useState(true);

  const [leadInput, setLeadInput] = useState({
    email: "",
    firstName: "",
    lastName: "",
    birthDay: "0000-00-00",
    birthTime: "00:00:00",
    signId: 0,
    gender: "",
    birthCountry: "",
    birthState: "",
    birthCity: "",
    birthPlaceId: 0,
  });
  const [customerInput, setCustomerInput] = useState({
    email: "",
    firstName: "",
    lastName: "",
    birthDay: "0000-00-00",
    birthTime: "00:00:00",
    signId: 0,
    gender: "",
    birthCountry: "",
    birthState: "",
    birthCity: "",
    birthPlaceId: 0,
  });
  const [leadBirthDate, setLeadBirthDate] = useState(null);
  const [customerBirthDate, setCustomerBirthDate] = useState(null);
  const [leadResultStatus, setLeadResultStatus] = useState({
    status: false,
    message: "",
    alertType: "danger",
  });
  const [customerResultStatus, setCustomerResultStatus] = useState({
    status: false,
    message: "",
    alertType: "danger",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchData = async (url, setInput, setResultStatus, isCustomer) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.status === "success") {
        console.log(data.data);
        // Get the full birth country name from the response
        const birthCountryValue = data.data.birthCountry;
        let birthCountryOption = countries.find(
          (option) => option.value === birthCountryValue
        );

        // If no result is found, check to see if the response contains the wow_country short name, and map the value to the full birth country name
        if (!birthCountryOption) {
          birthCountryOption = countries.find(
            (option) => option.astrowowId === birthCountryValue
          );
        }

        // Set default values for birthTime and birthDay if they are not provided
        const { birthDay = "1970-01-01", birthTime = "00:00:00" } = data.data;
        const birthCountry = birthCountryOption?.value || birthCountryValue;

        setInput({
          ...data.data,
          birthTime,
          birthCountry,
        });

        const birthDate = new Date(birthDay);

        if (isCustomer) {
          setCustomerBirthDate(birthDate);
          dispatch(allActions.userActions.setCustomer(data.data));
        } else {
          setLeadBirthDate(birthDate);
          dispatch(allActions.userActions.setLead(data.data));
        }

        setResultStatus({
          status: true,
          message: "",
          alertType: "",
        });
      } else {
        setResultStatus({
          status: true,
          message: "Profile data not found",
          alertType: "danger",
        });

        dispatch(allActions.userActions.setLead());
        dispatch(allActions.userActions.setCustomer());
      }
    } catch (error) {
      localStorage.clear();
      navigate("/login");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      await fetchData(
        `${apiLeadUrl}`,
        setLeadInput,
        setLeadResultStatus,
        false
      );

      await fetchData(
        `${apiCustomerUrl}`,
        setCustomerInput,
        setCustomerResultStatus,
        true
      );

      setIsLoading(false);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchterm]);

  const handleUpdate = async (url, input, setResultStatus, isCustomer) => {
    try {
      // Create a new object with only the desired fields
      const {
        email,
        firstName,
        lastName,
        birthDay,
        birthTime,
        signId,
        gender,
        birthCountry,
        birthCity,
        birthState,
        birthPlaceId,
      } = input;
      const formattedRequestBody = {
        email,
        firstName,
        lastName,
        birthDay,
        birthTime,
        signId,
        gender,
        birthCountry,
        birthState,
        birthCity,
        birthPlaceId,
      };

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedRequestBody),
      });

      const data = await response.json();

      if (data.status === "success") {
        isCustomer
          ? dispatch(allActions.userActions.setCustomer(formattedRequestBody))
          : dispatch(allActions.userActions.setLead(formattedRequestBody));

        setResultStatus({
          status: true,
          message: "Profile saved successfully",
          alertType: "success",
        });
      } else if (data.status === "error" && data.message === "Not Updated") {
        setResultStatus({
          status: true,
          message: "Nothing to update",
          alertType: "warning",
        });
      } else {
        setResultStatus({
          status: true,
          message: "Failed to save profile",
          alertType: "danger",
        });
      }
    } catch (error) {
      localStorage.clear();
      navigate("/login");
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      const email = encodeURIComponent(searchterm);
      const response = await fetch(
        `https://contacts.astrologyanswers.com/contacts/${email}`,
        {
          method: "DELETE",
          headers: {
            ApiKey: process.env.REACT_APP_CONTACTS_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        alert("Contact deleted successfully");
        // You might want to redirect or refresh the page here
      } else {
        alert("Failed to delete contact");
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      alert("An error occurred while deleting the contact");
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div className="app-wrapper">
      <div className="app-content pt-3 p-md-3 p-lg-4">
        <div className="container-xl">
          <div className="app-content pt-3 mb-5">
            {searchterm ? (
              <h3>
                Search: <code>{searchterm}</code>
              </h3>
            ) : (
              <h3>There is no name in the query string</h3>
            )}
          </div>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div>
                {leadResultStatus.message !== "Profile data not found" ? (
                  <button
                    type="button"
                    className="btn search-btn btn-danger col-2 align-middle me-1 mb-4"
                    onClick={handleDelete}
                  >
                    <span>Delete Contact</span>
                  </button>
                ) : (
                  <></>
                )}
              </div>
              {showDeleteConfirm && (
                <div
                  className="modal"
                  style={{
                    display: "block",
                    backgroundColor: "rgba(0,0,0,0.5)",
                  }}
                >
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Confirm Delete</h5>
                      </div>
                      <div className="modal-body">
                        <p>Are you sure you want to delete this contact?</p>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={cancelDelete}
                          autoFocus
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={confirmDelete}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="row g-3 mb-4 align-items-start justify-content-between">
                <div className="col-md-6">
                  {leadResultStatus.message === "Profile data not found" ? (
                    <div className="alert alert-danger">
                      Lead profile not found
                    </div>
                  ) : (
                    <>
                      <ProfileForm
                        title="Lead Profile"
                        input={leadInput}
                        setInput={setLeadInput}
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleUpdate(
                            apiLeadUrl,
                            leadInput,
                            setLeadResultStatus,
                            false
                          );
                        }}
                        starSignOptions={starSigns}
                        genderOptions={[
                          { label: "Please Select", value: "" },
                          { label: "Male", value: "m" },
                          { label: "Female", value: "f" },
                        ]}
                        countryOptions={countries}
                        stateOptions={usStates}
                        isStateVisible={
                          leadInput.birthCountry === "United States"
                        }
                        onCountryChange={(e) =>
                          setLeadInput((prev) => ({
                            ...prev,
                            birthCountry: e.target.value,
                            birthCity: "",
                            birthPlaceId: 0,
                          }))
                        }
                        onStateChange={(e) =>
                          setLeadInput((prev) => ({
                            ...prev,
                            birthState: e.target.value,
                            birthCity: "",
                            birthPlaceId: 0,
                          }))
                        }
                        onCityChange={(e) =>
                          setLeadInput((prev) => ({
                            ...prev,
                            birthCity: e.target.value,
                          }))
                        }
                        onAutoCompleteSelect={(city) =>
                          setLeadInput((prev) => ({
                            ...prev,
                            birthCity: city.label,
                            birthPlaceId: city.value,
                          }))
                        }
                        birthDate={leadBirthDate}
                        setBirthDate={setLeadBirthDate}
                        onBirthTimeChange={(time) =>
                          setLeadInput((prev) => ({
                            ...prev,
                            birthTime: `${time}:00`,
                          }))
                        }
                        isLoading={isLoading}
                        resultStatus={leadResultStatus}
                      />
                      <div className="mt-3">
                        <Link to={attributionLink}>
                          Click Here to View Lead Attribution Data
                        </Link>
                      </div>
                    </>
                  )}
                </div>
                <div className="col-md-6">
                  {customerResultStatus.message === "Profile data not found" ? (
                    <div className="alert alert-danger">
                      Customer profile not found
                    </div>
                  ) : (
                    <ProfileForm
                      title="Customer Profile"
                      input={customerInput}
                      setInput={setCustomerInput}
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleUpdate(
                          apiCustomerUrl,
                          customerInput,
                          setCustomerResultStatus,
                          true
                        );
                      }}
                      starSignOptions={starSigns}
                      genderOptions={[
                        { label: "Please Select", value: "" },
                        { label: "Male", value: "m" },
                        { label: "Female", value: "f" },
                      ]}
                      countryOptions={countries}
                      stateOptions={usStates}
                      isStateVisible={
                        customerInput.birthCountry === "United States"
                      }
                      onCountryChange={(e) =>
                        setCustomerInput((prev) => ({
                          ...prev,
                          birthCountry: e.target.value,
                          birthCity: "",
                          birthPlaceId: 0,
                        }))
                      }
                      onStateChange={(e) =>
                        setCustomerInput((prev) => ({
                          ...prev,
                          birthState: e.target.value,
                          birthCity: "",
                          birthPlaceId: 0,
                        }))
                      }
                      onCityChange={(e) =>
                        setCustomerInput((prev) => ({
                          ...prev,
                          birthCity: e.target.value,
                        }))
                      }
                      onAutoCompleteSelect={(city) =>
                        setCustomerInput((prev) => ({
                          ...prev,
                          birthCity: city.label,
                          birthPlaceId: city.value,
                        }))
                      }
                      birthDate={customerBirthDate}
                      setBirthDate={setCustomerBirthDate}
                      onBirthTimeChange={(time) =>
                        setCustomerInput((prev) => ({
                          ...prev,
                          birthTime: `${time}:00`,
                        }))
                      }
                      isLoading={isLoading}
                      resultStatus={customerResultStatus}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSection;
